import { throttle } from '../../../utils/throttle'

/**
 * Header Component
 *
 * Need a way to know if the Header has been stickied
 */
export class GMXHeader extends HTMLElement {
	navContainer : HTMLElement

	private lastYPos = 0
	private pastNavPoint : number
	private isScrollUpStickySM: boolean
	private barHeight : number

	private intersectionObserver: IntersectionObserver

	private boundSubNavScrollHandler = throttle(this._handleSubNavScroll.bind(this), 500, { leading: true, trailing: true})

	connectedCallback(): void {
		//Check if Sticky is true
		this.isScrollUpStickySM = (this.dataset['scrollUpStickySm'] === 'true');
		this.navContainer = this.children[0] as HTMLElement;

		if(this.isScrollUpStickySM) {
			this.pastNavPoint = this.offsetTop + (this.offsetHeight * 0.2);
			this.barHeight = this.clientHeight;

			window.addEventListener('scroll', this.boundSubNavScrollHandler)

			this.intersectionObserver = new IntersectionObserver(entries => this._handleObservationCallback(entries),
			{
				root: null,
				threshold: .5, // half in view to match pastNavPoint which is calculated as half the height of the component
			})
			this.intersectionObserver.observe(this)
		}
	}

	disconnectedCallback(): void {
		if(this.isScrollUpStickySM) {
			window.removeEventListener('scroll', this.boundSubNavScrollHandler)
		}

		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect()
		}
	}

	/**
	 * IntersectionObserver callback to reset the state of the header when it comes back into view. This is needed because if
	 * you scroll past the header, then scroll up and the header becomes fixed/sticky, if you scroll up past the header position,
	 * the header should become unsticky but the scroll event is throttled so it won't update until after the throttle delay.  This
	 * ensures that as soon as the header position is back in the view, the sticky header goes away.
	 * @param entries
	 * @private
	 */
	private _handleObservationCallback(entries: IntersectionObserverEntry[]) {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				this.navContainer.classList.remove('scroll-up')
				this.navContainer.classList.remove('scroll-down')
				this.navContainer.style.transform = ''
				this.style.removeProperty('height')
			}
		})
	}

	private _handleSubNavScroll() {
		// Only show the bar with the animation if:
		// 1) either we haven't shown it so far, or have scroll down after scrolling up
		// 2) we've scrolled UP
		// 3) we're BELOW it is normal DOM position
		// 4.) AND the scroll hasn't been disabled by an overflow/modal display event
		if( (!this.navContainer.classList.contains("scroll-up") ||
				this.navContainer.classList.contains("scroll-down")) &&
			window.scrollY < this.lastYPos &&
			window.scrollY > this.pastNavPoint &&
			!document.body.classList.contains('scroll-disabled')) {
			// Need to set the height to maintain the page 'flow' so that the page doesn't "jump"
			// when you scroll up and the inner nav container becomes fixed
			// So this is when we set the height for the container but
			// add a class to make the child fixed and run the animation to move it down from the top
			this.style.height = `${this.barHeight}px`;
			this.navContainer.classList.add("scroll-up");
			this.navContainer.classList.remove("scroll-down");
			this.navContainer.style.transform = `translateY(0%)`
		}

		// Remove our scroll up class IF:
		// 1) the bar is being shown AND
		// 2) we're scrolling UP
		// 3) we're ABOVE the normal DOM position
		if( this.navContainer.classList.contains("scroll-up") &&
			window.scrollY < this.lastYPos &&
			window.scrollY < this.pastNavPoint ) {
			this.style.removeProperty("height")
			this.navContainer.classList.remove("scroll-up");
			this.navContainer.classList.remove("scroll-down");
			this.navContainer.style.transform = ''
		}

		// There is a 3rd case where we need to animate the bar up:
		// 1) If the bar is being shown AND
		// 2) If you scroll DOWN AND
		// 3) If you are BELOW the normal DOM position
		if( this.navContainer.classList.contains("scroll-up") &&
			window.scrollY > this.lastYPos &&
			window.scrollY > this.pastNavPoint ) {
			this.navContainer.classList.remove("scroll-up");
			this.navContainer.classList.add("scroll-down");
			this.navContainer.style.transform = `translateY(-100%)`
		}

		// If we are below where the subnav element is on the page, we should set the position offset so on
		// scroll up, we get the transition
		if (!this.navContainer.classList.contains("scroll-up") &&
			!this.navContainer.classList.contains("scroll-down") &&
			window.scrollY > (this.pastNavPoint + this.navContainer.offsetHeight)) {
			this.style.height = `${this.barHeight}px`;
			this.navContainer.style.transform = `translateY(-100%)`
		}

		this.lastYPos = window.scrollY;
	};
}

if (!customElements.get('gmx-header')) {
	customElements.define('gmx-header', GMXHeader)
}
